import React, { useState } from 'react';
import {
    AdminPageLayout,
    Card,
    CardHeader,
    CardTitle,
    CardContent,
    Button,
    Grid,
    Col,
    Row,
    StatGrid,
    StatCard,
    FormInput,
    FormActions,
    Section,
    Badge
} from '../../components';
import {
    Listing,
    ListingActionBar,
    ListingFilter,
    ListingItem,
    ListingCard
} from '../../components/listing/v2';
import {
    CreateAction,
    EditAction,
    ViewAction,
    DeleteAction,
    ExportAction
} from '../../components/listing/v2/ListingAction';
import {
    Layers,
    Box,
    Layout,
    Activity,
    Database,
    Users,
    Shield,
    Settings,
    Plus,
    Search,
    CheckCircle,
    Clock,
    TrendingUp,
    AlertCircle,
    Zap,
    Target,
    Eye,
    Edit3,
    Trash2,
    Save,
    X,
    ArrowLeft,
    Download
} from 'lucide-react';

const LayoutDemo = () => {
    const [viewVariant, setViewVariant] = useState('grid');
    const [loading, setLoading] = useState(false);

    // Sample data for demo
    const sampleItems = [
        { id: 1, title: 'Premier League Alpha', subtitle: 'Youth development program', players: 45, status: 'active' },
        { id: 2, title: 'Junior Kicks V2', subtitle: 'Beginner training sessions', players: 32, status: 'active' },
        { id: 3, title: 'Elite Strides', subtitle: 'Competitive age group focus', players: 28, status: 'inactive' },
        { id: 4, title: 'Academy Beta', subtitle: 'Professional track training', players: 15, status: 'active' },
    ];

    return (
        <AdminPageLayout
            title="Standardized Design System"
            subtitle="Comprehensive guide to layout, contrast, and components"
            backPath="/admin/club"
            maxWidth="1400px" // Explicitly showing the consistent max width
        >
            {/* 0. Button Styling Standards */}
            <div className="demo-section">
                <h2 className="section-title"><Target size={20} /> Button Styling Standards</h2>
                <p className="section-desc">
                    Standardized button variants for consistent user experience across the platform.
                    Each variant has semantic meaning and appropriate visual treatment.
                </p>

                <h3 style={{ fontSize: '1rem', marginBottom: '1.5rem', opacity: 0.85 }}>Primary & Secondary Actions</h3>
                <Grid columns={3} gap="1.5rem">
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Primary</h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem', minHeight: '40px' }}>Main call-to-action. High contrast with glow effect.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <Button variant="primary" size="sm">Create New</Button>
                            <Button variant="primary" size="md">Submit Form</Button>
                            <Button variant="primary" size="lg">Get Started</Button>
                        </div>
                    </div>
                    
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Secondary</h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem', minHeight: '40px' }}>Alternative actions with lower visual weight.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <Button variant="secondary" size="sm">Learn More</Button>
                            <Button variant="secondary" size="md">View Details</Button>
                            <Button variant="secondary" size="lg">Explore</Button>
                        </div>
                    </div>
                    
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Outline</h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem', minHeight: '40px' }}>Minimal visual weight for tertiary actions.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <Button variant="outline" size="sm">Filter</Button>
                            <Button variant="outline" size="md">Options</Button>
                            <Button variant="outline" size="lg">Settings</Button>
                        </div>
                    </div>
                </Grid>

                <h3 style={{ fontSize: '1rem', margin: '3rem 0 1.5rem', opacity: 0.85 }}>Semantic Action Variants</h3>
                <Grid columns={3} gap="1.5rem">
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Eye size={16} /> View
                        </h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem', minHeight: '40px' }}>Info/blue - for viewing and reading actions.</p>
                        <Button variant="view" icon={<Eye size={18} />}>View Details</Button>
                    </div>
                    
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Edit3 size={16} /> Edit
                        </h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem', minHeight: '40px' }}>Cyan - for editing and modification actions.</p>
                        <Button variant="edit" icon={<Edit3 size={18} />}>Edit Record</Button>
                    </div>
                    
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Save size={16} /> Save
                        </h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem', minHeight: '40px' }}>Green - for saving and submitting forms.</p>
                        <Button variant="save" icon={<Save size={18} />}>Save Changes</Button>
                    </div>
                    
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Trash2 size={16} /> Delete
                        </h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem', minHeight: '40px' }}>Magenta - for destructive actions (delete, deactivate).</p>
                        <Button variant="delete" icon={<Trash2 size={18} />}>Delete Item</Button>
                    </div>
                    
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <X size={16} /> Cancel
                        </h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem', minHeight: '40px' }}>Gray - for canceling and going back.</p>
                        <Button variant="cancel" icon={<X size={18} />}>Cancel</Button>
                    </div>
                    
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <AlertCircle size={16} /> Warning
                        </h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem', minHeight: '40px' }}>Purple - for warnings and caution actions.</p>
                        <Button variant="warning" icon={<AlertCircle size={18} />}>Proceed Anyway</Button>
                    </div>
                </Grid>

                <h3 style={{ fontSize: '1rem', margin: '3rem 0 1.5rem', opacity: 0.85 }}>Special Variants</h3>
                <Grid columns={2} gap="1.5rem">
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Ghost</h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem' }}>Transparent background, minimal visual presence.</p>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <Button variant="ghost" size="sm">Skip</Button>
                            <Button variant="ghost" size="sm" icon={<Download size={16} />}>Download</Button>
                            <Button variant="ghost" size="sm" icon={<ArrowLeft size={16} />}>Back</Button>
                        </div>
                    </div>
                    
                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl1)', borderRadius: '12px', border: '1px solid var(--color-border)' }}>
                        <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>States</h4>
                        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem' }}>Disabled and loading states.</p>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <Button variant="primary" disabled>Disabled</Button>
                            <Button variant="primary" loading>Loading...</Button>
                        </div>
                    </div>
                </Grid>

                <div style={{ 
                    marginTop: '2.5rem', 
                    padding: '1.5rem', 
                    background: 'var(--surface-lvl2)', 
                    borderRadius: '12px',
                    border: '1px solid var(--color-border)'
                }}>
                    <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Box size={18} /> Usage Guidelines
                    </h4>
                    <Grid columns={2} gap="1rem">
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Theme Color Variables</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                All semantic actions use CSS variables (--color-action-*) for consistent theming
                            </p>
                        </div>
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Listing Context</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                In listings/grids, use outline style with same semantic colors (cyan for edit, blue for view)
                            </p>
                        </div>
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Size Consistency</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                Use 'xs' in listings, 'sm' in compact UIs, 'md' (default) for forms, 'lg' for hero sections
                            </p>
                        </div>
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Semantic Meaning</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                Use semantic variants (save, delete, edit, view) consistently for all CRUD operations
                            </p>
                        </div>
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Visual Hierarchy</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                One primary action per section, use outline/secondary for alternatives
                            </p>
                        </div>
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Icon Support</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                Use Lucide icons (16-18px) for better recognition, place before text
                            </p>
                        </div>
                    </Grid>
                </div>
            </div>

            {/* 1. Status Cards - Modern Sleek Design */}
            <div className="demo-section" style={{ marginTop: '4rem' }}>
                <h2 className="section-title"><TrendingUp size={20} /> Modern Status Cards</h2>
                <p className="section-desc">
                    Sleek, modern status cards with enhanced visual hierarchy, gradient effects, and surface elevation.
                    Supports both React icon components (Lucide) and Font Awesome icon strings for maximum flexibility.
                </p>

                <h3 style={{ fontSize: '1rem', marginBottom: '1.5rem', opacity: 0.85 }}>Default Variant (Primary Green)</h3>
                <StatGrid>
                    <StatCard
                        number="1,284"
                        label="Total Players"
                        detail="8% increase this month"
                        icon={<Users size={24} />}
                    />
                    <StatCard
                        number="156"
                        label="Active Sessions"
                        detail="across all programs"
                        icon={<Target size={24} />}
                    />
                    <StatCard
                        number="$42,350"
                        label="Revenue"
                        detail="this quarter"
                        icon={<TrendingUp size={24} />}
                    />
                    <StatCard
                        number="89%"
                        label="Satisfaction"
                        detail="based on feedback"
                        icon={<Zap size={24} />}
                    />
                </StatGrid>

                <h3 style={{ fontSize: '1rem', margin: '3rem 0 1.5rem', opacity: 0.85 }}>Color Variants</h3>
                <StatGrid>
                    <StatCard
                        number="42"
                        label="Active Programs"
                        detail="across 6 locations"
                        icon={<Layout size={24} />}
                        variant="primary"
                    />
                    <StatCard
                        number="98%"
                        label="Attendance Rate"
                        detail="average for all squads"
                        icon={<CheckCircle size={24} />}
                        variant="success"
                    />
                    <StatCard
                        number="15"
                        label="Pending Tasks"
                        detail="requires immediate action"
                        icon={<Clock size={24} />}
                        variant="warning"
                    />
                    <StatCard
                        number="3"
                        label="Critical Issues"
                        detail="needs resolution"
                        icon={<AlertCircle size={24} />}
                        variant="error"
                    />
                </StatGrid>

                <h3 style={{ fontSize: '1rem', margin: '3rem 0 1.5rem', opacity: 0.85 }}>Horizontal Layout</h3>
                <Grid columns={2} gap="1.5rem">
                    <StatCard
                        number="2,450"
                        label="Total Members"
                        detail="415 joined this month"
                        icon={<Users size={24} />}
                        layout="horizontal"
                    />
                    <StatCard
                        number="$156K"
                        label="Annual Revenue"
                        detail="+24% year over year"
                        icon={<TrendingUp size={24} />}
                        layout="horizontal"
                        variant="success"
                    />
                </Grid>

                <h3 style={{ fontSize: '1rem', margin: '3rem 0 1.5rem', opacity: 0.85 }}>Loading State</h3>
                <Grid columns={4} gap="1.5rem">
                    <StatCard
                        number="--"
                        label="Loading Data"
                        detail="Please wait..."
                        icon={<Activity size={24} />}
                        loading
                    />
                    <StatCard
                        number="--"
                        label="Loading Data"
                        detail="Please wait..."
                        icon={<Database size={24} />}
                        variant="primary"
                        loading
                    />
                    <StatCard
                        number="--"
                        label="Loading Data"
                        detail="Please wait..."
                        icon={<Shield size={24} />}
                        variant="success"
                        loading
                    />
                    <StatCard
                        number="--"
                        label="Loading Data"
                        detail="Please wait..."
                        icon={<Settings size={24} />}
                        variant="warning"
                        loading
                    />
                </Grid>

                <div style={{ 
                    marginTop: '2.5rem', 
                    padding: '1.5rem', 
                    background: 'var(--surface-lvl2)', 
                    borderRadius: '12px',
                    border: '1px solid var(--color-border)'
                }}>
                    <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Box size={18} /> Design Features
                    </h4>
                    <Grid columns={2} gap="1rem">
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Surface Elevation</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                Uses design tokens (--surface-lvl1) for consistent depth
                            </p>
                        </div>
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Gradient Numbers</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                Gradient text with webkit-background-clip for modern aesthetics
                            </p>
                        </div>
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Icon Flexibility</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                Supports both Lucide React icons and Font Awesome strings
                            </p>
                        </div>
                        <div>
                            <strong style={{ fontSize: '0.9rem' }}>✓ Glow Effects</strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                Subtle shadow glows on icons enhance visual hierarchy
                            </p>
                        </div>
                    </Grid>
                </div>
            </div>

            {/* 2. Dashboard Pattern - Visual Contrast & Stats */}
            <div className="demo-section" style={{ marginTop: '4rem' }}>
                <h2 className="section-title"><Activity size={20} /> Dashboard & Metrics Patterns</h2>
                <p className="section-desc">Stat cards provide high visual contrast and immediate visibility into key metrics.</p>

                <StatGrid>
                    <StatCard
                        number="1,284"
                        label="Total Players"
                        detail="8% increase this month"
                        icon={<Users size={24} />}
                    />
                    <StatCard
                        number="42"
                        label="Active Programs"
                        detail="across 6 locations"
                        icon={<Layout size={24} />}
                        variant="primary"
                    />
                    <StatCard
                        number="98%"
                        label="Attendance Rate"
                        detail="average for all squads"
                        icon={<CheckCircle size={24} />}
                        variant="success"
                    />
                    <StatCard
                        number="15"
                        label="Pending Tasks"
                        detail="requires immediate action"
                        icon={<Clock size={24} />}
                        variant="warning"
                    />
                </StatGrid>
            </div>

            {/* 3. Listing V2 Patterns - Grid vs List */}
            <div className="demo-section" style={{ marginTop: '4rem' }}>
                <h2 className="section-title"><Database size={20} /> Data Listing (V2 System)</h2>
                <p className="section-desc">Standardized patterns for CRUD pages, supporting both Grid and List views with actions.</p>

                <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem' }}>
                    <Button
                        variant={viewVariant === 'grid' ? 'primary' : 'outline'}
                        onClick={() => setViewVariant('grid')}
                        size="sm"
                    >Grid View</Button>
                    <Button
                        variant={viewVariant === 'list' ? 'primary' : 'outline'}
                        onClick={() => setViewVariant('list')}
                        size="sm"
                    >List View</Button>
                </div>

                <Card level={1} padding="0">
                    <ListingActionBar
                        leftSection={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ position: 'relative' }}>
                                    <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }} />
                                    <input
                                        type="text"
                                        placeholder="Search records..."
                                        style={{
                                            padding: '0.6rem 0.6rem 0.6rem 2.5rem',
                                            background: 'var(--surface-lvl2)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: '8px',
                                            color: 'var(--color-text)',
                                            width: '300px'
                                        }}
                                    />
                                </div>
                            </div>
                        }
                        rightSection={
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <ExportAction size="sm" />
                                <CreateAction size="sm" label="Add Record" />
                            </div>
                        }
                    />

                    <Listing
                        items={sampleItems}
                        variant={viewVariant}
                        renderItem={(item) => (
                            <ListingItem
                                key={item.id}
                                title={item.title}
                                subtitle={item.subtitle}
                                variant={viewVariant}
                                icon={<Box size={24} />}
                                iconVariant={item.status === 'active' ? 'primary' : 'secondary'}
                                actions={
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <ViewAction size="xs" />
                                        <EditAction size="xs" />
                                        <DeleteAction size="xs" />
                                    </div>
                                }
                            >
                                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                                        <Users size={14} /> {item.players} Players
                                    </div>
                                    <Badge variant={item.status === 'active' ? 'success' : 'secondary'}>
                                        {item.status.toUpperCase()}
                                    </Badge>
                                </div>
                            </ListingItem>
                        )}
                    />
                </Card>
                
                <div style={{ 
                    marginTop: '1.5rem', 
                    padding: '1.25rem', 
                    background: 'var(--surface-lvl1)', 
                    borderRadius: '12px',
                    border: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                        <CheckCircle size={20} style={{ color: 'var(--color-primary)', flexShrink: 0, marginTop: '2px' }} />
                        <div>
                            <strong style={{ fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                                Semantic Color Consistency
                            </strong>
                            <p style={{ fontSize: '0.85rem', opacity: 0.8, margin: 0 }}>
                                Notice how View, Edit, and Delete actions use their semantic colors consistently:
                                <span style={{ color: 'var(--color-action-view)', fontWeight: 600 }}> blue for viewing</span>,
                                <span style={{ color: 'var(--color-action-edit)', fontWeight: 600 }}> cyan for editing</span>, and
                                <span style={{ color: 'var(--color-action-delete)', fontWeight: 600 }}> magenta for deletion</span>.
                                The outline style provides good contrast in listing contexts without overwhelming the content.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. CRUD Detail / Form Patterns */}
            <div className="demo-section" style={{ marginTop: '4rem' }}>
                <h2 className="section-title"><Shield size={20} /> Management & Detail Patterns</h2>
                <p className="section-desc">Standardized layouts for editing records or viewing deep details.</p>

                <Grid columns={12} gap="2rem">
                    <Col span={8}>
                        <Card level={1}>
                            <CardHeader>
                                <CardTitle>Resource Configuration</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={(e) => e.preventDefault()}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                        <FormInput
                                            label="Resource Name"
                                            placeholder="Enter resource name"
                                            defaultValue="Alpha Training Program"
                                        />
                                        <FormInput
                                            label="Capacity Limit"
                                            type="number"
                                            placeholder="Default capacity"
                                            defaultValue="50"
                                        />
                                    </div>
                                    <div style={{ marginTop: '1.5rem' }}>
                                        <FormInput
                                            label="Description"
                                            type="textarea"
                                            placeholder="Describe this resource..."
                                            defaultValue="Comprehensive youth training focused on technical skills."
                                            rows={4}
                                        />
                                    </div>

                                    <div style={{ marginTop: '2rem' }}>
                                        <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <Layers size={18} /> Advanced Options (Nested)
                                        </h4>
                                        <Card level={2} style={{ border: '1px solid var(--color-border-light)' }}>
                                            <CardContent>
                                                <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>These options are tucked into a Level 2 surface to reduce visual load.</p>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                                    <div style={{ padding: '0.75rem', background: 'var(--surface-lvl3)', borderRadius: '8px' }}>
                                                        <strong>Level 3 Item</strong>
                                                        <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>Extra config A</div>
                                                    </div>
                                                    <div style={{ padding: '0.75rem', background: 'var(--surface-lvl3)', borderRadius: '8px' }}>
                                                        <strong>Level 3 Item</strong>
                                                        <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>Extra config B</div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    <FormActions
                                        onCancel={() => { }}
                                        submitLabel="Update Program"
                                    />
                                </form>
                            </CardContent>
                        </Card>
                    </Col>
                    <Col span={4}>
                        <Card level={1}>
                            <CardHeader>
                                <CardTitle>Summary & Status</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        background: 'var(--color-primary-background)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 1.5rem',
                                        color: 'var(--color-primary)'
                                    }}>
                                        <Layout size={40} />
                                    </div>
                                    <h3>Alpha Program</h3>
                                    <Badge variant="success" style={{ marginTop: '0.5rem' }}>ACTIVE</Badge>
                                </div>

                                <div style={{ marginTop: '2rem', borderTop: '1px solid var(--color-border)', paddingTop: '1.5rem' }}>
                                    <div className="summary-row">
                                        <span>Creation Date</span>
                                        <strong>Jan 15, 2024</strong>
                                    </div>
                                    <div className="summary-row">
                                        <span>Owner</span>
                                        <strong>Club Admin</strong>
                                    </div>
                                    <div className="summary-row">
                                        <span>Last Updated</span>
                                        <strong>2 hours ago</strong>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card level={2} style={{ marginTop: '1.5rem' }}>
                            <CardHeader style={{ padding: '1rem', borderBottom: '1px solid var(--color-border-light)' }}>
                                <CardTitle as="h5" style={{ fontSize: '1rem' }}><Settings size={16} /> Quick Settings</CardTitle>
                            </CardHeader>
                            <CardContent style={{ padding: '1rem' }}>
                                <p style={{ fontSize: '0.85rem' }}>Enable public visibility for this program.</p>
                                <Button variant="outline" size="sm" style={{ width: '100%', marginTop: '0.5rem' }}>Configure Domain</Button>
                            </CardContent>
                        </Card>
                    </Col>
                </Grid>
            </div>

            {/* 5. Platform & Club Management Patterns */}
            <div className="demo-section" style={{ marginTop: '4rem', marginBottom: '4rem' }}>
                <h2 className="section-title"><Settings size={20} /> Platform & Club Management</h2>
                <p className="section-desc">Specific patterns for high-density management interfaces, featuring complex status indicators and nested resource grids.</p>

                <Grid columns={3} gap="2rem">
                    <Card level={1}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                            <div>
                                <h4 style={{ margin: 0 }}>Custom Domain</h4>
                                <code style={{ fontSize: '0.8rem', opacity: 0.6 }}>soccer.alpha-club.com</code>
                            </div>
                            <Badge variant="success">PROVISIONED</Badge>
                        </div>
                        <div style={{ background: 'var(--surface-lvl2)', padding: '1rem', borderRadius: '8px' }}>
                            <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem', opacity: 0.7 }}>SSL Certificate</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <CheckCircle size={14} style={{ color: 'var(--color-success)' }} />
                                <span style={{ fontSize: '0.9rem' }}>Valid until Dec 2024</span>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" style={{ width: '100%', marginTop: '1.5rem' }}>Manage TLS</Button>
                    </Card>

                    <Card level={1}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                            <h4 style={{ margin: 0 }}>System Resources</h4>
                            <Badge variant="warning">85% LOAD</Badge>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.3rem' }}>
                                    <span>Storage</span>
                                    <span>1.2TB / 2TB</span>
                                </div>
                                <div style={{ height: '6px', background: 'var(--surface-lvl3)', borderRadius: '3px', overflow: 'hidden' }}>
                                    <div style={{ width: '60%', height: '100%', background: 'var(--color-primary)' }}></div>
                                </div>
                            </div>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.3rem' }}>
                                    <span>Database Ops</span>
                                    <span>450 req/s</span>
                                </div>
                                <div style={{ height: '6px', background: 'var(--surface-lvl3)', borderRadius: '3px', overflow: 'hidden' }}>
                                    <div style={{ width: '85%', height: '100%', background: 'var(--color-warning)' }}></div>
                                </div>
                            </div>
                        </div>
                        <Button variant="ghost" size="sm" style={{ width: '100%', marginTop: '1.5rem' }}>View Analytics</Button>
                    </Card>

                    <Card level={1} variant="feature">
                        <h4 style={{ marginBottom: '1rem' }}>Support & Security</h4>
                        <p style={{ fontSize: '0.85rem', marginBottom: '1.5rem', opacity: 0.8 }}>Advanced security protocols are active for this tenant.</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <Badge variant="primary" size="sm">2FA REQUIRED</Badge>
                            <Badge variant="primary" size="sm">IP WHITELIST</Badge>
                            <Badge variant="primary" size="sm">AUDIT LOGS</Badge>
                        </div>
                        <Button variant="primary" size="sm" style={{ width: '100%', marginTop: '2.3rem' }}>Security Portal</Button>
                    </Card>
                </Grid>
            </div>

            {/* 6. Modals & Dialogs Patterns */}
            <div className="demo-section" style={{ marginTop: '4rem', marginBottom: '4rem' }}>
                <h2 className="section-title"><Box size={20} /> Modals & Dialogs</h2>
                <p className="section-desc">Standardized patterns for overlays, ensuring high contrast and clear calls to action.</p>

                <Grid columns={3} gap="2rem">
                    <Card level={1}>
                        <h4 style={{ marginBottom: '1rem' }}>Confirmation Dialogs</h4>
                        <p style={{ fontSize: '0.85rem', marginBottom: '1.5rem', opacity: 0.8 }}>Standardized confirmation flows for data operations.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <Button variant="outline" size="sm" onClick={() => { }}>Trigger Info Dialog</Button>
                            <Button variant="warning" size="sm" onClick={() => { }}>Trigger Warning Dialog</Button>
                            <Button variant="delete" size="sm" onClick={() => { }}>Trigger Danger Dialog</Button>
                        </div>
                    </Card>

                    <Card level={1}>
                        <h4 style={{ marginBottom: '1rem' }}>Form Modals</h4>
                        <p style={{ fontSize: '0.85rem', marginBottom: '1.5rem', opacity: 0.8 }}>Handling sub-flows or complex adjustments without leaving context.</p>
                        <Button variant="primary" size="sm" style={{ width: '100%' }} onClick={() => { }}>Open Resource Modal</Button>
                        <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--surface-lvl2)', borderRadius: '8px', border: '1px dashed var(--color-border)' }}>
                            <div style={{ fontSize: '0.75rem', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Best Practice</div>
                            <div style={{ fontSize: '0.85rem' }}>Use "Medium" size for forms and "Small" for confirmations.</div>
                        </div>
                    </Card>

                    <Card level={1}>
                        <h4 style={{ marginBottom: '1rem' }}>Aesthetics</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--color-primary)' }}></div>
                                <span style={{ fontSize: '0.85rem' }}>Glassmorphism Backdrop (4px blur)</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--surface-lvl1)', border: '1px solid var(--color-border)' }}></div>
                                <span style={{ fontSize: '0.85rem' }}>Surface Border Accents</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--color-secondary)' }}></div>
                                <span style={{ fontSize: '0.85rem' }}>Clear Visual Hierarchy</span>
                            </div>
                        </div>
                    </Card>
                </Grid>
            </div>

            <style jsx="true">{`
        .demo-section {
          width: 100%;
          margin: 0 auto;
        }
        .section-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
          color: var(--color-primary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-size: 1.1rem;
        }
        .section-desc {
          margin-bottom: 2rem;
          opacity: 0.8;
          max-width: 1000px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.75rem;
          font-size: 0.9rem;
        }
        .summary-row span {
          opacity: 0.7;
        }
        .input-group {
          background: var(--surface-lvl2);
          padding: 1.5rem;
          border-radius: 12px;
          border: 1px solid var(--color-border-light);
        }
      `}</style>
        </AdminPageLayout>
    );
};

export default LayoutDemo;
